// REBUY PRODUCT-FAMILY GROUPING (Oofos-style stitching)
// ----------------------------------------------------------------
// Junk Food groups sibling color products via the metafield `custom.product_family`.
// Instead of a custom swatch UI, we merge sibling variants + images into the seed
// product so Rebuy's native `selectVariantByOption` + `displayColorSwatches` UI
// handles the rest. The seed product's `options[Color].values` is extended to
// include every sibling's color, and a `swatchColors` lookup is built from each
// sibling's `*_sw.jpg` swatch image.

(function () {
  function extractProductId(gid) {
    if (!gid) return null;
    const match = String(gid).match(/\/(\d+)(?:\?|$)/);
    return match ? match[1] : null;
  }

  function valueHandle(value) {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  // Finds the swatch image on a sibling product. Mirrors snippets/product-block-product-family.liquid (iterates sibling.images, matches image_alt contains "swatch"). Falls back to `_sw` filename match.
  function findSiblingSwatch(siblingProduct) {
    const images = (siblingProduct && siblingProduct.images) || [];
    return images.find((img) => {
      const alt = String((img && img.alt) || '').toLowerCase();
      const src = String((img && img.src) || '').toLowerCase();
      return alt.includes('swatch') || src.includes('_sw');
    });
  }

  // Merges sibling Rebuy products into the seed: union of variants, union of images, extends the Color option's values to cover every sibling color, builds product.swatchColors lookup keyed by color value.
  function mergeSiblingsIntoSeed(seed, rebuyProducts) {
    if (!seed || !Array.isArray(rebuyProducts) || rebuyProducts.length === 0) return;

    seed.images = rebuyProducts.flatMap((p) =>
      (p.images || []).map((img, idx) => ({ ...img, position: idx })),
    );

    seed.variants = rebuyProducts.flatMap((p) =>
      (p.variants || []).map((variant) => ({
        ...variant,
        // Leave variant.title intact (e.g. "Tie Dye / S") so Rebuy's formatVariantOptionTitle renders a useful select label. Parent product title is exposed separately for any consumer that needs it.
        productTitle: p.title,
        image_id: p.image && p.image.id,
        image_url: p.image && p.image.src,
        productTags: p.tags,
      })),
    );

    const colorIdx = (seed.options || []).findIndex(
      (o) => String(o.name).toLowerCase() === 'color',
    );
    if (colorIdx < 0) return;
    const optionKey = `option${colorIdx + 1}`;

    seed.options[colorIdx].values = [
      ...new Set(seed.variants.map((v) => v[optionKey]).filter(Boolean)),
    ];

    // Build swatchColors keyed by color value, iterating siblings per-product (matches snippets/product-block-product-family.liquid: each sibling has its own swatch image with alt containing "swatch").
    seed.swatchColors = {};
    rebuyProducts.forEach((p) => {
      const variant = (p.variants || [])[0];
      const colorValue = variant && variant[optionKey];
      if (!colorValue) return;
      const swatchImg = findSiblingSwatch(p);
      if (swatchImg) {
        seed.swatchColors[colorValue] = { value: swatchImg.src, image: true };
      } else {
        seed.swatchColors[colorValue] = {
          value: String(colorValue).split(' ').pop().toLowerCase(),
          image: false,
        };
      }
    });
  }

  async function stitchOne(seedProduct) {
    if (!seedProduct) return;
    if (seedProduct.productGrouping) return; // already stitched, skip
    if (!window.shopifyClient || !window.rebuyClient || !seedProduct.handle) return;

    const data = await window.shopifyClient.getProductFamily(seedProduct.handle);
    const refs = data?.product?.metafield?.references?.nodes;
    if (!Array.isArray(refs) || refs.length === 0) {
      seedProduct.productGrouping = [];
      return;
    }

    const grouped = refs.map((g) => ({ ...g, productId: extractProductId(g.id) }));
    const ids = grouped.map((g) => g.productId).filter(Boolean);
    const rebuyProducts = await window.rebuyClient.getProductsByIDs(ids);

    mergeSiblingsIntoSeed(seedProduct, rebuyProducts);
    seedProduct.productGrouping = grouped;
  }

  async function stitchWidgetProducts(widget) {
    if (!widget || !widget.data || !Array.isArray(widget.data.products)) return;
    await Promise.all(widget.data.products.map(stitchOne));
    if (widget.View && widget.View.vue && typeof widget.View.vue.$forceUpdate === 'function') {
      widget.View.vue.$forceUpdate();
    }
  }

  // Returns the second non-swatch media image url for the currently-selected color's grouping member (mirrors Oofos's hover-image binding). Null when no grouping match or no second image.
  window.rebuyHoverImage = function (product) {
    if (!product || !product.selected_variant) return null;
    const pid = String(product.selected_variant.product_id || '');
    if (!pid) return null;
    const grouping = product.productGrouping || [];
    const match = grouping.find((g) => g && g.id && String(g.id).includes(pid));
    if (!match || !match.media || !Array.isArray(match.media.nodes)) return null;
    const imgs = match.media.nodes
      .filter((n) => n && n.image && n.image.url && !String(n.image.url).toLowerCase().includes('_sw'))
      .map((n) => n.image.url);
    return imgs[1] || null;
  };

  document.addEventListener('rebuy.productsChange', async (event) => {
    const widget = event.detail && event.detail.widget;
    if (!widget) return;
    try {
      await stitchWidgetProducts(widget);
    } catch (err) {
      console.error('rebuy-grouping: stitch error', err);
    }
  });
})();
